import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPaperPlane} from '@fortawesome/free-solid-svg-icons/faPaperPlane';

import {Textarea, FormHandler} from 'Components/Form';
import {Alert} from 'Components/Partials';
import {Card, CardBody} from 'Components/Card';

import {Socket, User} from 'Services';
import Question from 'Services/Api/MainStage/Question';
import {translation} from 'Services/TranslationHelpers';
import {faTrash} from '@fortawesome/free-solid-svg-icons/faTrash';
import QuestionsApi from 'Services/Api/Admin/MainStage/Question';

class Questions extends React.Component {
    /**
     * @var containerRef
     * @type {null|HTMLDivElement}
     */
    containerRef = null;

    /**
     * @var titleRef
     * @type {null|HTMLDivElement}
     */
    titleRef = null;

    /**
     * @var formRef
     * @type {null|HTMLDivElement}
     */
    formRef = null;

    /**
     * @var success
     * @type {string}
     */
    success = translation('main_stage_frontend', 'thank_you_for_submission');

    /**
     * @var state
     * @type {{questions: array}}
     */
    state = {
        questions: [],
    }

    /**
     * @method componentDidMount
     */
    async componentDidMount() {
        await this.fetchQuestions();
        this.subscribeToBroadcasts();

        if (window.base.main_stage.layout === 'tabs') {
            this.setSize();
        }
    };

    /**
     * @method componentDidUpdate
     * @param {object} prevProps
     */
    componentDidUpdate = (prevProps) => {
        if (window.base.main_stage.layout === 'tabs' && prevProps.height !== this.props.height) {
            this.setSize();
        }
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        if (this.alertHider) {
            clearTimeout(this.alertHider)
        }

        Socket.echo.leave(`main_stage_question.${this.props.sessionId}`);
    }

    /**
     * @method setSize
     */
    setSize = () => {
        if (this.props.readOnly) {
            this.containerRef.style.height = `${this.props.height - this.titleRef.clientHeight - 48}px`;
        } else {
            this.containerRef.style.height = `${this.props.height - this.formRef.clientHeight - this.titleRef.clientHeight - 48}px`;
        }
    };

    /**
     * @method fetchQuestions
     */
    fetchQuestions = async () => {
        let params = {};

        if (!this.props.readOnly) {
            params = {
                user_id: User.id,
            };
        }

        const questions = await Question.get({
            session: this.props.sessionId,
        }, params);

        if (!questions.success) {
            return;
        }

        this.setState({
            questions: questions.data.data,
        });
    }

    /**
     * @method subscribeToBroadcasts
     */
    subscribeToBroadcasts = () => {
        Socket.getConnection()
            .private(`main_stage_question.${this.props.sessionId}`)
            .listen('MainStageQuestionApproved', (e) => {
                if (!this.props.readOnly) {
                    if (User.id !== e.question.user_id) {
                        return;
                    }
                }

                if (this.state.questions.filter(q => q.id === e.question.id).length > 0) {
                    return;
                }

                this.setState({
                    questions: [
                        ...[e.question],
                        ...this.state.questions,
                    ],
                });
            })
            .listen('MainStageQuestionDeleted', (e) => {
                this.setState({
                    questions: this.state.questions.filter(q => q.id !== e.question.id),
                });
            });
    }

    /**
     * @method handleSubmit
     * @param {object} form
     * @return {*}
     */
    handleSubmit = (form) => {
        return Question.post({
            session: this.props.sessionId,
        }, form).then((response) => {
            this.alertHider = setTimeout(() => {
                this.props.resetAlert();
            }, 5000);

            return response;
        });
    };

    deleteQuestion = async (id, key) => {
        const {questions} = this.state;
        
        await QuestionsApi.delete({
            session: this.state.sessionId,
            lastResource: id,
        });


        questions.splice(key, 1);

        this.setState({
            questions,
        })
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {questions} = this.state;

        return (
            <Card className="h-full bg-gray-200">
                <CardBody className="h-full flex flex-grow flex-col">
                    <h1 className="text-2xl font-bold" ref={ref => this.titleRef = ref}>
                        {translation('main_stage_frontend', 'submit_a_question')}
                    </h1>

                    <div
                        className={`overflow-y-scroll py-2 ${window.base.main_stage.layout === 'tabs' ? '' : (this.props.readOnly ? 'h-full' : 'h-32')}`}
                        ref={ref => this.containerRef = ref}>
                        {questions.map((q, k) => (
                            <div key={q.id} className="mb-2 p-4 flex flex-col bg-gray-500 bg-opacity-20 rounded-md">
                                <div className="font-semibold flex flex-row">
                                    <div>{q.user.full_name}</div>
                                    {User.data.is_admin && (
                                        <div className="ml-auto">
                                            <button onClick={() => this.deleteQuestion(q.id, k)}>
                                                <FontAwesomeIcon icon={faTrash}/>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    {q.question}
                                </div>
                            </div>
                        ))}
                    </div>

                    {!this.props.readOnly && this.renderForm()}
                </CardBody>
            </Card>
        );
    }

    /**
     * @method renderForm
     * @return {JSX.Element}
     */
    renderForm = () => {
        const {
            form,
            alert,
            working,
            handleSubmit,
            handleInput,
        } = this.props;

        let placeholder = translation('main_stage_frontend', 'type_question');

        return (
            <form
                ref={ref => this.formRef = ref}
                className="relative flex flex-col justify-between"
                onSubmit={(e) => handleSubmit(e, this.handleSubmit, this.success, true)}
            >
                {alert && (
                    <div className="w-full rounded-md absolute z-10 -translate-y-full">
                        <Alert containerStyles="p-4" {...alert} />
                    </div>
                )}

                <div className="relative">
                    <Textarea
                        placeholder={placeholder}
                        rows={5}
                        onChange={(v) => handleInput('question', v)}
                        value={form.question ?? ''}
                    />

                    <FontAwesomeIcon
                        icon={faPaperPlane}
                        size="1x"
                        onClick={() => !working && form.question ? handleSubmit(
                            null,
                            this.handleSubmit,
                            this.success,
                            true,
                        ) : false}
                        className="absolute top-8 right-6 text-black cursor-pointer"
                    />
                </div>
            </form>
        )
    }
}

export default FormHandler(Questions);
